import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { ApiError } from 'src/types/interfaces/api-error';
import { delayedCallbackExecution } from 'src/utils/delayed-callback-execution';
import { isApiErrorMessage } from '../../../../api/utils/is-api-error-message';
import { ApiErrorMessage } from '../../../employees-groups/api/types/api-error-message';
import { spacesApiErrorsTranslations } from '../constants/api-errors-translations';
import { uploadSpaces } from '../lib/spaces';
import { spacesKeys } from '../query-keys/spaces';

const parseMessage = (message: string) => {
  return message.split(',').reduce(
    (acc, entry) => {
      const [key, value] = entry.split(':');
      acc[key.trim()] = parseInt(value.trim());
      return acc;
    },
    {} as Record<string, number>,
  );
};

export const useUploadSpaces = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['spaces', 'common']);

  function hasDetailedValidationInMessage(message: string) {
    return message.includes('SPACES_WITHOUT_NAME');
  }

  function showDetailedValidationInfoMessage(message: string) {
    const entries = parseMessage(message);

    const problems = [];

    if (entries.SPACES_WITHOUT_NAME > 0) {
      problems.push(
        t('spaces:common.uploadSpacesDialog.problems.spacesWithoutName', {
          count: entries.SPACES_WITHOUT_NAME,
        }),
      );
    }

    if (entries.SPACES_WITHOUT_FLOOR > 0) {
      problems.push(
        t('spaces:common.uploadSpacesDialog.problems.spacesWithoutFloor', {
          count: entries.SPACES_WITHOUT_FLOOR,
        }),
      );
    }

    if (entries.SPACES_WITH_INVALID_TYPE > 0) {
      problems.push(
        t('spaces:common.uploadSpacesDialog.problems.spacesWithInvalidType', {
          count: entries.SPACES_WITH_INVALID_TYPE,
        }),
      );
    }

    if (entries.SPACES_WITH_INVALID_CAPACITY > 0) {
      problems.push(
        t(
          'spaces:common.uploadSpacesDialog.problems.spacesWithInvalidCapacity',
          {
            count: entries.SPACES_WITH_INVALID_CAPACITY,
          },
        ),
      );
    }

    toast.error(
      t('spaces:common.uploadSpacesDialog.problemWithData', {
        problems: problems.join(', '),
      }),
    );
  }

  return useMutation({
    mutationFn: (file: File) => uploadSpaces(file),
    onSuccess: () => {
      delayedCallbackExecution(1000, () => {
        queryClient.invalidateQueries({
          queryKey: spacesKeys.lists(),
        });
      });
      toast.success(t('spaces:common.uploadSpacesDialog.success'));

      onSuccess?.();
    },
    onError: (error: ApiError) => {
      const errorMessage = error?.response?.data?.message;

      const hasCodedValidationMessage =
        hasDetailedValidationInMessage(errorMessage);

      if (hasCodedValidationMessage) {
        showDetailedValidationInfoMessage(errorMessage);

        return;
      }

      if (
        isApiErrorMessage<ApiErrorMessage>(
          errorMessage,
          spacesApiErrorsTranslations,
        )
      ) {
        toast.error(t(spacesApiErrorsTranslations[errorMessage]));

        return;
      }

      toast.error(
        error?.response?.data?.message ||
          t('common:errorMessages.somethingWentWrong'),
      );
    },
  });
};
